import { boardFromString, boardToString, type Difficulty } from '../sudoku';
import type { GameState } from './types';

export interface NewGame {
  key: string;
  kind: 'daily' | 'level';
  difficulty: Difficulty;
  givens: number[];
}

const empty81 = (): number[][] => Array.from({ length: 81 }, () => []);

export function initGame({ key, kind, difficulty, givens }: NewGame): GameState {
  return {
    key,
    kind,
    difficulty,
    givens: [...givens],
    cells: [...givens],
    notes: empty81(),
    selected: null,
    notesMode: false,
    elapsedMs: 0,
    running: true,
    status: 'playing',
    mistakes: 0,
    hintsUsed: 0,
    past: [],
    future: [],
  };
}

/** Compact, JSON-safe representation persisted locally and synced to the server. */
export interface StoredGame {
  key: string;
  kind: 'daily' | 'level';
  difficulty: Difficulty;
  givens: string;
  current: string;
  notes: number[][];
  elapsedMs: number;
  hintsUsed: number;
  mistakes: number;
  status: GameState['status'];
  updatedAt: number;
}

export function serialize(state: GameState, updatedAt: number): StoredGame {
  return {
    key: state.key,
    kind: state.kind,
    difficulty: state.difficulty,
    givens: boardToString(state.givens),
    current: boardToString(state.cells),
    notes: state.notes,
    elapsedMs: state.elapsedMs,
    hintsUsed: state.hintsUsed,
    mistakes: state.mistakes,
    status: state.status,
    updatedAt,
  };
}

export function deserialize(stored: StoredGame): GameState {
  const givens = boardFromString(stored.givens);
  return {
    key: stored.key,
    kind: stored.kind,
    difficulty: stored.difficulty,
    givens,
    cells: boardFromString(stored.current),
    notes: stored.notes.length === 81 ? stored.notes.map((n) => [...n]) : empty81(),
    selected: null,
    notesMode: false,
    elapsedMs: stored.elapsedMs,
    running: false, // resumed games start paused until the player acts
    status: stored.status,
    mistakes: stored.mistakes,
    hintsUsed: stored.hintsUsed,
    past: [],
    future: [],
  };
}
